import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, PhoneForwarded, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const SESSION_KEY = "pr_ai_session";

const GREETING = {
  role: "assistant",
  content:
    "Hey — I'm your Plenty Rydez Concierge. Ask me anything: fleet capacity, pricing, deposits, mileage, rideshare rentals. If I miss the mark, tap 'Escalate to human' below and a teammate will jump in.",
};

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [showEscForm, setShowEscForm] = useState(false);
  const [esc, setEsc] = useState({ name: "", email: "", phone: "", reason: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, escalating, showEscForm]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: text });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "I hit a snag. Please try again or tap 'Escalate to human' below.",
      }]);
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    setEscalating(true);
    try {
      const { data } = await api.post("/ai/escalate", {
        session_id: sessionId || `aic_${Date.now()}`,
        name: esc.name.trim() || undefined,
        email: esc.email.trim() || undefined,
        phone: esc.phone.trim() || undefined,
        reason: esc.reason.trim() || undefined,
      });
      setEscalated(true);
      setShowEscForm(false);
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Escalated to a Plenty Rydez teammate (ticket ${data.ticket_id}). We'll reach out via ${esc.email || esc.phone || "the contact you provided"} shortly.`,
      }]);
      toast.success("Escalated to a human — we'll be in touch");
    } catch (e) {
      toast.error("Could not escalate right now. Please email louisv@plenty-rydez.com");
    } finally {
      setEscalating(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          data-testid="concierge-chat-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 rounded-full bg-gold text-ink-900 px-5 py-3 shadow-[0_20px_60px_rgba(249,201,124,0.35)] font-semibold text-sm inline-flex items-center gap-2 hover:brightness-110 transition"
          aria-label="Open concierge chat"
        >
          <MessageCircle size={16} /> Concierge · 24/7
        </button>
      )}

      {open && (
        <div
          data-testid="concierge-chat-panel"
          className="fixed bottom-6 right-6 z-40 w-[92vw] sm:w-[400px] h-[560px] rounded-3xl bg-ink-900 border border-white/12 shadow-[0_40px_120px_rgba(0,0,0,0.55)] flex flex-col overflow-hidden"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-white/8 bg-gradient-to-r from-gold/15 to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                <Sparkles size={14} className="text-gold" />
              </div>
              <div className="leading-tight">
                <div className="font-serif text-base">Plenty Rydez Concierge</div>
                <div className="text-[10px] label-caps text-white/50">AI · 24/7 · Escalates to human</div>
              </div>
            </div>
            <button
              type="button"
              data-testid="concierge-chat-close"
              onClick={() => setOpen(false)}
              className="text-white/50 hover:text-white transition"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </header>

          <div ref={scrollRef} data-testid="concierge-chat-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i}
                className={`max-w-[85%] text-sm rounded-2xl px-3 py-2 leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-gold text-ink-900"
                    : "mr-auto bg-white/[.06] text-white/85 border border-white/8"
                }`}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="mr-auto max-w-[85%] rounded-2xl px-3 py-2 bg-white/[.06] border border-white/8 text-white/60 text-sm inline-flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Thinking…
              </div>
            )}
            {showEscForm && !escalated && (
              <div className="rounded-2xl border border-electric/30 bg-electric/5 p-3 space-y-2">
                <div className="label-caps text-electric text-[10px]">Escalate to human</div>
                <input value={esc.name} onChange={(e) => setEsc({ ...esc, name: e.target.value })}
                  data-testid="esc-name"
                  placeholder="Your name" className="chat-input" />
                <input value={esc.email} onChange={(e) => setEsc({ ...esc, email: e.target.value })}
                  data-testid="esc-email" type="email"
                  placeholder="Email" className="chat-input" />
                <input value={esc.phone} onChange={(e) => setEsc({ ...esc, phone: e.target.value })}
                  data-testid="esc-phone" type="tel"
                  placeholder="Phone (optional)" className="chat-input" />
                <textarea value={esc.reason} onChange={(e) => setEsc({ ...esc, reason: e.target.value })}
                  data-testid="esc-reason"
                  placeholder="What do you need help with?" rows={2} className="chat-input" />
                <button type="button"
                  data-testid="esc-submit"
                  onClick={escalate}
                  disabled={escalating || (!esc.email.trim() && !esc.phone.trim())}
                  className="w-full bg-electric text-ink-900 rounded-full py-2 text-xs font-semibold hover:brightness-110 disabled:opacity-50">
                  {escalating ? "Sending…" : "Send to Plenty Rydez team"}
                </button>
              </div>
            )}
          </div>

          <div className="border-t border-white/8 p-3 bg-ink-950/50 space-y-2">
            {!escalated && !showEscForm && (
              <button type="button"
                data-testid="concierge-escalate-btn"
                onClick={() => setShowEscForm(true)}
                className="w-full text-xs text-white/60 hover:text-white inline-flex items-center justify-center gap-1.5 py-1">
                <PhoneForwarded size={12} /> Escalate to human
              </button>
            )}
            <form onSubmit={send} className="flex gap-2">
              <input
                data-testid="concierge-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about fleet, deposits, mileage…"
                className="chat-input flex-1"
                disabled={sending}
              />
              <button
                type="submit"
                data-testid="concierge-chat-send"
                disabled={sending || !input.trim()}
                className="w-10 h-10 rounded-full bg-gold text-ink-900 flex items-center justify-center disabled:opacity-40"
                aria-label="Send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </form>
          </div>
        </div>
      )}
      <style>{`
        .chat-input {
          background: #121214; border: 1px solid rgba(255,255,255,0.08);
          color: #fff; border-radius: 12px; padding: 8px 12px;
          font-size: 13px; outline: none; transition: border-color 0.2s;
          width: 100%;
        }
        .chat-input:focus { border-color: #F9C97C; }
        .chat-input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </>
  );
}
